/* eslint-disable react/prop-types */
import React, {
  useEffect, useReducer,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import SuccessStoriesReducer, { initialState } from './reducer';
import ACTIONS_TYPE from './actionType';
import { getPartnerDetail, getPartnersSuccessStories } from '../../../services/partner.service';
import { getPartnerDetailMappedSuccessStories } from '../../../utils/success-stories.util';
import { getKebabCase } from '../../../utils/srp.util';
import { BASENAME } from '../../constants';
import { pageViewEvent } from '../../analyticsApi';
import { getBaseURL } from '../../utilities';

export const SuccessStoryContext = React.createContext(initialState);

const getInitialState = (store) => {
  const { errors = {} } = store || {};
  const { storyError = true } = errors || {};
  return storyError ? initialState : store;
};

export function SuccessStoriesContextProvider({
  children, store, goBack, setGoBack,
}) {
  const [state, dispatch] = useReducer(SuccessStoriesReducer, getInitialState(store));
  const navigate = useNavigate();
  const { id, locale } = useParams();
  // eslint-disable-next-line no-unused-vars
  const [translationsUpdated, setTranslationsUpdated] = useState(false);

  useEffect(() => {
    if (goBack) {
      setGoBack(false);
      const { partnerDetail } = state || {};
      const { display_name: displayName, id: partnerId } = partnerDetail || {};
      if (displayName && id) {
        const defaultPath = `${BASENAME}/${getKebabCase(displayName)}/${partnerId}`;
        const baseUrl = getBaseURL(locale, defaultPath);
        navigate(baseUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goBack]);

  const getTextFromData = (detailToLoop, keys) => {
    const textToTranslate = [];
    Object.keys(detailToLoop).forEach((element) => {
      if (keys.includes(element) && detailToLoop[element]) {
        textToTranslate.push({
          source: detailToLoop[element],
        });
      }
    });
    return textToTranslate;
  };

  const getTranslatedSuccessStory = async (successStory, partnerDetail) => {
    const partnerTranslationKeys = ['display_name', 'headline'];
    const storyTranslationKeys = ['conclusion', 'business_outcomes', 'solutions', 'project_goals', 'title', 'customer_challenge'];
    const storyLoopingKeys = ['autodesk_solutions', 'services_provided', 'customer_industries'];
    let textToTranslate = getTextFromData(partnerDetail, partnerTranslationKeys);
    textToTranslate = [...textToTranslate, ...getTextFromData(successStory, storyTranslationKeys)];
    storyLoopingKeys.forEach((key) => {
      if (successStory?.[key]?.length) {
        successStory[key].forEach((element) => {
          textToTranslate.push({
            source: element,
          });
        });
      }
    });
    if (partnerDetail?.moreSuccessStories?.length) {
      partnerDetail.moreSuccessStories.forEach((element) => {
        if (element.title) {
          textToTranslate.push({ source: element.title });
        }
        if (element.customer_challenge) {
          textToTranslate.push({ source: element.customer_challenge });
        }
      });
    }
    dispatch({ type: ACTIONS_TYPE.SET_TRANSLATION_TEXT, payload: textToTranslate });
  };

  const clearSateDispatch = () => {
    dispatch({ type: ACTIONS_TYPE.GET_SUCCESS_STORIES, payload: initialState.successStories });
    dispatch({ type: ACTIONS_TYPE.GET_PARTNER_DETAIL, payload: initialState.partnerDetail });
  };

  const fetchSuccessStoriesData = async (localParamsId = null) => {
    dispatch({ type: ACTIONS_TYPE.SUCCESS_STORIES_LOADING, payload: true });
    await clearSateDispatch();
    try {
      const { data } = await getPartnersSuccessStories(`${localParamsId || id}`);
      dispatch({ type: ACTIONS_TYPE.GET_SUCCESS_STORIES, payload: data || {} });

      const response = await getPartnerDetail(data.provider_id);
      const partnerDetail = getPartnerDetailMappedSuccessStories(response?.data || {}, data.provider_id, data.id);
      getTranslatedSuccessStory(data, partnerDetail);
      dispatch({ type: ACTIONS_TYPE.GET_PARTNER_DETAIL, payload: partnerDetail });
      dispatch({ type: ACTIONS_TYPE.SUCCESS_STORIES_LOADING, payload: false });
    } catch (error) {
      console.error('Error loading success story:', error);
    } finally {
      dispatch({ type: ACTIONS_TYPE.SUCCESS_STORIES_LOADING, payload: false });
    }
  };

  useEffect(() => {
    const fetchAPi = async () => {
      pageViewEvent();
      const { successStories, errors } = store || {};
      if (successStories?.id) {
        getTranslatedSuccessStory(state.successStories, state.partnerDetail);
      }
      if (!errors || errors?.storyError || (id && (!successStories || successStories?.id !== Number(id)))) {
        await fetchSuccessStoriesData();
      }
    };
    fetchAPi();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Helmet>
        <title>
          {store?.successStories?.title || state?.successStories?.title || 'Success Story'}
          {' '}
          | Autodesk Support
        </title>
      </Helmet>
      <SuccessStoryContext.Provider
        value={{
          loading: state.loading,
          successStories: state.successStories,
          partnerDetail: state.partnerDetail,
          fetchSuccessStoriesData,
          dispatch,
          textToTranslate: state.textToTranslate,
        }}
      >
        {children}
      </SuccessStoryContext.Provider>
    </>
  );
}

SuccessStoriesContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  store: PropTypes.shape({}),
  goBack: PropTypes.bool,
  setGoBack: PropTypes.func,
};

SuccessStoriesContextProvider.defaultProps = {
  store: {},
  goBack: false,
  setGoBack: () => {},
};

/* eslint-disable react/jsx-props-no-spreading */
export const SuccessStoriesContextConsumer = (Component) => (props) => (
  <SuccessStoryContext.Consumer>
    {(context) => <Component {...props} {...context} />}
  </SuccessStoryContext.Consumer>
);
